// Definición de los PASOS del onboarding guiado (tour de bienvenida).
//
// Cada paso describe un hito del recorrido: a qué ruta navegar, qué elemento del
// DOM resaltar (por su atributo `data-tour`), y las claves i18n del título y el
// cuerpo del mensaje. Un paso puede exigir una funcionalidad (`feature`): si el
// usuario no tiene permiso de lectura sobre ella, el paso se OMITE (el tour solo
// muestra lo que el usuario realmente puede usar). Un paso sin `target` se
// muestra centrado (modal de bienvenida/cierre), sin spotlight.
//
// Los textos NO se hardcodean aquí: se referencian por clave i18n y viven en los
// catálogos de `src/i18n/locales/*.json` bajo el namespace `onboarding.steps`.

import type { Feature } from '../types.js';

/** Un paso del recorrido guiado. */
export interface OnboardingStep {
  /** Identificador estable del paso (para el atributo data-tour y para debug). */
  id: string;
  /**
   * Ruta a la que navegar antes de mostrar el paso. Si se omite, no se navega
   * (se queda en la vista actual).
   */
  route?: string;
  /**
   * Selector del atributo `data-tour` del elemento a resaltar. Si se omite, el
   * mensaje se muestra centrado (sin spotlight), útil para bienvenida/cierre.
   */
  target?: string;
  /** Funcionalidad requerida: si el usuario no la tiene, el paso se omite. */
  feature?: Feature | null;
  /** Clave i18n del título del paso. */
  titleKey: string;
  /** Clave i18n del cuerpo (mensaje) del paso. */
  bodyKey: string;
  /** Icono (Material Symbols) decorativo del mensaje. */
  icon?: string;
  /**
   * Ubicación preferida del tooltip respecto al elemento resaltado. El motor lo
   * ajusta si no cabe; `auto` deja que decida según el espacio disponible.
   */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto' | 'center';
}

/**
 * Guion del recorrido en orden. Arranca con una bienvenida centrada, recorre las
 * pestañas principales (catálogo, consulta rápida, alta por foto, wishlist) y el
 * menú de módulos, y cierra invitando a explorar. Cada paso de funcionalidad se
 * ancla a su pestaña/entrada de navegación, de modo que el usuario aprenda dónde
 * encontrar cada cosa. Pasos con `feature` se omiten si no hay permiso.
 */
export const ONBOARDING_STEPS: ReadonlyArray<OnboardingStep> = [
  // ── Bienvenida ────────────────────────────────────────────────────────────
  {
    id: 'welcome',
    placement: 'center',
    icon: 'waving_hand',
    titleKey: 'onboarding.steps.welcome.title',
    bodyKey: 'onboarding.steps.welcome.body',
  },

  // ── 1. Catálogo (sección principal + subfunciones) ──────────────────────────
  {
    id: 'catalog',
    route: '/',
    target: 'nav-catalog',
    feature: 'catalog',
    icon: 'album',
    placement: 'top',
    titleKey: 'onboarding.steps.catalog.title',
    bodyKey: 'onboarding.steps.catalog.body',
  },
  {
    id: 'catalog-search-modes',
    route: '/',
    target: 'catalog-search-modes',
    feature: 'catalog',
    icon: 'manage_search',
    placement: 'bottom',
    titleKey: 'onboarding.steps.catalogSearch.title',
    bodyKey: 'onboarding.steps.catalogSearch.body',
  },
  {
    id: 'catalog-sort',
    route: '/',
    target: 'catalog-sort',
    feature: 'catalog',
    icon: 'sort',
    placement: 'bottom',
    titleKey: 'onboarding.steps.catalogSort.title',
    bodyKey: 'onboarding.steps.catalogSort.body',
  },

  // ── 2. Alta de disco (sección principal + subfunciones) ─────────────────────
  {
    id: 'add',
    target: 'nav-add',
    feature: 'ingestion',
    icon: 'add_circle',
    placement: 'top',
    titleKey: 'onboarding.steps.add.title',
    bodyKey: 'onboarding.steps.add.body',
  },
  {
    id: 'add-capture-modes',
    route: '/add',
    target: 'add-capture-modes',
    feature: 'ingestion',
    icon: 'photo_camera',
    placement: 'bottom',
    titleKey: 'onboarding.steps.addCapture.title',
    bodyKey: 'onboarding.steps.addCapture.body',
  },
  {
    id: 'add-search-discogs',
    route: '/add',
    target: 'add-search-discogs',
    feature: 'ingestion',
    icon: 'auto_awesome',
    placement: 'top',
    titleKey: 'onboarding.steps.addProposal.title',
    bodyKey: 'onboarding.steps.addProposal.body',
  },

  // ── 3. Consulta rápida (sección principal + subfunciones) ───────────────────
  {
    id: 'quick-lookup',
    target: 'nav-search',
    feature: 'quick_lookup',
    icon: 'search',
    placement: 'top',
    titleKey: 'onboarding.steps.quickLookup.title',
    bodyKey: 'onboarding.steps.quickLookup.body',
  },
  {
    id: 'lookup-input-modes',
    route: '/quick-lookup',
    target: 'lookup-input-modes',
    feature: 'quick_lookup',
    icon: 'category',
    placement: 'bottom',
    titleKey: 'onboarding.steps.lookupModes.title',
    bodyKey: 'onboarding.steps.lookupModes.body',
  },
  {
    id: 'lookup-run',
    route: '/quick-lookup',
    target: 'lookup-run',
    feature: 'quick_lookup',
    icon: 'radar',
    placement: 'top',
    titleKey: 'onboarding.steps.lookupRun.title',
    bodyKey: 'onboarding.steps.lookupRun.body',
  },

  // ── 4. Lista de deseados (sección principal + subfunciones) ─────────────────
  {
    id: 'wishlist',
    target: 'nav-wishlist',
    feature: 'wishlist',
    icon: 'bookmark',
    placement: 'top',
    titleKey: 'onboarding.steps.wishlist.title',
    bodyKey: 'onboarding.steps.wishlist.body',
  },
  {
    id: 'wishlist-filters',
    route: '/wishlist',
    target: 'wishlist-filters',
    feature: 'wishlist',
    icon: 'filter_list',
    placement: 'bottom',
    titleKey: 'onboarding.steps.wishlistFilters.title',
    bodyKey: 'onboarding.steps.wishlistFilters.body',
  },
  {
    id: 'wishlist-only-super',
    route: '/wishlist',
    target: 'wishlist-only-super',
    feature: 'wishlist',
    icon: 'priority_high',
    placement: 'bottom',
    titleKey: 'onboarding.steps.wishlistSuper.title',
    bodyKey: 'onboarding.steps.wishlistSuper.body',
  },

  // ── Más herramientas + cierre ───────────────────────────────────────────────
  {
    id: 'menu',
    target: 'nav-menu',
    feature: null,
    icon: 'grid_view',
    placement: 'top',
    titleKey: 'onboarding.steps.menu.title',
    bodyKey: 'onboarding.steps.menu.body',
  },
  // ── Paso imprescindible: crear la "bodega base" (primera ubicación) ─────────
  // Sin al menos una ubicación/segmento de numeración, un disco nuevo no cae en
  // ningún segmento y queda SIN numerar (ver `records.noSegment`). Por eso el
  // recorrido cierra llevando al usuario a crear su primera ubicación. Si no
  // tiene permiso sobre `locations`, el paso se omite como el resto.
  {
    id: 'base-location',
    route: '/locations',
    target: 'locations-new',
    feature: 'locations',
    icon: 'shelves',
    placement: 'bottom',
    titleKey: 'onboarding.steps.baseLocation.title',
    bodyKey: 'onboarding.steps.baseLocation.body',
  },

  {
    id: 'finish',
    placement: 'center',
    icon: 'celebration',
    titleKey: 'onboarding.steps.finish.title',
    bodyKey: 'onboarding.steps.finish.body',
  },
];
